import type {
  RoleplayExpression,
  RoleplayScorecard,
  RoleplayScorecardCategory,
  RoleplayScorecardMoment,
  RoleplayTurn,
} from 'services/api/roleplayClient';

export type ScoreTone = 'strong' | 'solid' | 'focus';

export const SCORE_COLORS: Record<ScoreTone, string> = {
  strong: 'var(--rp-score-strong)',
  solid: 'var(--rp-score-solid)',
  focus: 'var(--rp-score-focus)',
};

export const scoreTone = (score: number): ScoreTone => {
  if (score >= 80) return 'strong';
  if (score >= 60) return 'solid';
  return 'focus';
};

export const TIER_LABELS: Record<ScoreTone, string> = {
  strong: 'Strong',
  solid: 'Solid',
  focus: 'Focus area',
};

export type ResolvedMoment = {
  learnerTurn: number;
  learnerText: string;
  personaText: string | null;
  personaExpression: RoleplayExpression | null;
};

export type ScorecardCategoryView = RoleplayScorecardCategory & {
  tone: ScoreTone;
  tier: string;
  delta: number | null;
  moment: RoleplayScorecardMoment;
  resolvedMoment: ResolvedMoment | null;
};

export type ScorecardView = {
  headline: string;
  overallScore: number;
  overallTone: ScoreTone;
  categories: ScorecardCategoryView[];
  /** Distinct skills; null when no skill earns the highlight (see pickHighlights). */
  bestIndex: number | null;
  focusIndex: number | null;
  /** Lowest-scoring skill; the desktop detail panel opens on it. */
  lowestIndex: number;
  hasLastTry: boolean;
  turningPoint: { index: number; text: string; moment: ResolvedMoment | null } | null;
};

export const resolveMoment = (
  moment: RoleplayScorecardMoment | null | undefined,
  turns: RoleplayTurn[],
): ResolvedMoment | null => {
  if (!moment) return null;
  const learner = turns.find((turn) => turn.id === moment.learner_turn_id);
  if (!learner) return null;
  const persona = moment.persona_turn_id === null
    ? undefined
    : turns.find((turn) => turn.id === moment.persona_turn_id);
  return {
    learnerTurn: moment.learner_turn,
    learnerText: learner.text,
    personaText: persona?.text ?? null,
    personaExpression: persona?.persona_expression ?? null,
  };
};

const indexOfExtreme = (scores: number[], pick: (a: number, b: number) => boolean, skip = -1) =>
  scores.reduce<number>((best, score, index) => {
    if (index === skip) return best;
    return best < 0 || pick(score, scores[best]) ? index : best;
  }, -1);

/**
 * "Best move" is the top skill only when it is at least Solid; "Focus next" is
 * the lowest other skill only when it is below Strong. Ties never put the same
 * skill in both cards.
 */
export const pickHighlights = (scores: number[]): { bestIndex: number | null; focusIndex: number | null } => {
  const top = indexOfExtreme(scores, (a, b) => a > b);
  if (scores.length < 2 || top < 0) return { bestIndex: null, focusIndex: null };
  const bestIndex = scoreTone(scores[top]) === 'focus' ? null : top;
  const low = indexOfExtreme(scores, (a, b) => a < b, bestIndex ?? -1);
  const focusIndex = low >= 0 && scoreTone(scores[low]) !== 'strong' ? low : null;
  return { bestIndex, focusIndex };
};

export const buildScorecardView = (
  scorecard: RoleplayScorecard,
  turns: RoleplayTurn[],
): ScorecardView => {
  const categories = scorecard.categories.map((category) => {
    const tone = scoreTone(category.score);
    return {
      ...category,
      tone,
      tier: TIER_LABELS[tone],
      delta: category.last_score === null ? null : category.score - category.last_score,
      resolvedMoment: resolveMoment(category.moment, turns),
    };
  });
  const scores = categories.map((category) => category.score);
  const turningIndex = scorecard.turning_point
    ? categories.findIndex((category) => category.key === scorecard.turning_point?.category_key)
    : -1;
  return {
    headline: scorecard.headline,
    overallScore: scorecard.overall_score,
    overallTone: scoreTone(scorecard.overall_score),
    categories,
    ...pickHighlights(scores),
    lowestIndex: Math.max(0, indexOfExtreme(scores, (a, b) => a < b)),
    hasLastTry: categories.some((category) => category.last_score !== null),
    turningPoint: scorecard.turning_point && turningIndex >= 0
      ? {
        index: turningIndex,
        text: scorecard.turning_point.text,
        moment: resolveMoment(scorecard.turning_point.moment, turns),
      }
      : null,
  };
};

/** Usable scorecard payloads only; anything else keeps the plain debrief. */
export const isRenderableScorecard = (value: unknown): value is RoleplayScorecard => {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<RoleplayScorecard>;
  return typeof candidate.overall_score === 'number'
    && Array.isArray(candidate.categories)
    && candidate.categories.length > 0;
};
